// ============ RECURSION ALGORITHMS ============

export const generateFactorialSteps = (n) => {
  const steps = [];
  const callStack = [];

  steps.push({
    line: 2,
    description: `Starting factorial(${n})`,
    variables: { n },
    callStack: [],
    currentCall: null
  });

  // Build the call stack (going down)
  for (let i = n; i >= 1; i--) {
    callStack.push({ func: 'factorial', param: i, returnVal: null });
    steps.push({
      line: i === 1 ? 4 : 6,
      description: i === 1 
        ? `Base case reached: factorial(1) = 1` 
        : `Recursive call: factorial(${i}) → return ${i} * factorial(${i - 1})`,
      variables: { n: i, depth: n - i + 1 },
      callStack: [...callStack],
      currentCall: callStack.length - 1
    });
  }

  // Unwind the stack (going up) - compute return values
  let result = 1;
  callStack[callStack.length - 1].returnVal = 1;
  steps.push({
    line: 4,
    description: `factorial(1) returns 1`,
    variables: { returnValue: 1 },
    callStack: [...callStack],
    currentCall: callStack.length - 1,
    returning: true
  });

  for (let i = callStack.length - 2; i >= 0; i--) {
    result *= callStack[i].param;
    callStack[i].returnVal = result;
    steps.push({
      line: 6,
      description: `factorial(${callStack[i].param}) = ${callStack[i].param} * ${callStack[i + 1].returnVal} = ${result}`,
      variables: { returnValue: result, computed: `${callStack[i].param} * ${callStack[i + 1].returnVal}` },
      callStack: [...callStack],
      currentCall: i,
      returning: true
    });
  }

  steps.push({
    line: 8,
    description: `Final result: factorial(${n}) = ${result}`,
    variables: { result },
    callStack: [...callStack],
    currentCall: 0,
    result
  });

  return steps;
};

export const generateFibonacciSteps = (n) => {
  const steps = [];
  const memo = {};

  steps.push({
    line: 2,
    description: `Computing fibonacci(${n})`,
    variables: { n },
    callStack: [],
    memo: {}
  });

  const fib = (num, depth = 0) => {
    if (num in memo) return memo[num];
    
    const callId = { func: 'fib', param: num, returnVal: null };
    
    steps.push({
      line: num <= 1 ? 3 : 5,
      description: num <= 1 
        ? `Base case: fib(${num}) = ${num}` 
        : `Compute fib(${num}) = fib(${num - 1}) + fib(${num - 2})`,
      variables: { n: num, depth },
      callStack: [callId],
      currentCall: 0,
      memo: {...memo}
    });

    if (num <= 1) {
      memo[num] = num;
      steps.push({
        line: 3,
        description: `fib(${num}) returns ${num}`,
        variables: { returnValue: num },
        callStack: [callId],
        currentCall: 0,
        returning: true,
        memo: {...memo}
      });
      return num;
    }

    const a = fib(num - 1, depth + 1);
    const b = fib(num - 2, depth + 1);
    const result = a + b;
    memo[num] = result;

    steps.push({
      line: 5,
      description: `fib(${num}) = ${a} + ${b} = ${result}`,
      variables: { n: num, returnValue: result, computed: `${a} + ${b}` },
      callStack: [callId],
      currentCall: 0,
      returning: true,
      memo: {...memo}
    });

    return result;
  };

  const result = fib(n);

  steps.push({
    line: 7,
    description: `Final result: fibonacci(${n}) = ${result}`,
    variables: { result },
    callStack: [],
    memo: {...memo},
    result
  });

  return steps;
};

export const generateTowerOfHanoiSteps = (n) => {
  const steps = [];
  const pegs = {
    A: Array.from({ length: n }, (_, i) => n - i),
    B: [],
    C: []
  };
  let moveCount = 0;

  steps.push({
    line: 2,
    description: `Tower of Hanoi with ${n} disks. Move all disks from A to C using B.`,
    variables: { n },
    pegs: { A: [...pegs.A], B: [...pegs.B], C: [...pegs.C] },
    moveCount,
    callStack: []
  });

  const hanoi = (numDisks, source, target, aux, depth = 0) => {
    const callId = { func: 'hanoi', param: `${numDisks}, ${source}→${target}`, returnVal: null };
    
    steps.push({
      line: 5,
      description: `hanoi(${numDisks}, ${source}, ${target}, ${aux})`,
      variables: { disks: numDisks, source, target, aux, depth },
      pegs: { A: [...pegs.A], B: [...pegs.B], C: [...pegs.C] },
      moveCount,
      callStack: Array(depth + 1).fill(callId),
      currentCall: depth
    });

    if (numDisks === 1) {
      const disk = pegs[source].pop();
      pegs[target].push(disk);
      moveCount++;

      steps.push({
        line: 6,
        description: `Move disk ${disk} from ${source} to ${target}`,
        variables: { disk, from: source, to: target, moves: moveCount },
        pegs: { A: [...pegs.A], B: [...pegs.B], C: [...pegs.C] },
        moveCount,
        callStack: Array(depth + 1).fill(callId),
        currentCall: depth,
        movingDisk: { disk, from: source, to: target }
      });
      return;
    }

    hanoi(numDisks - 1, source, aux, target, depth + 1);
    
    const disk = pegs[source].pop();
    pegs[target].push(disk);
    moveCount++;
    steps.push({
      line: 10,
      description: `Move disk ${disk} from ${source} to ${target}`,
      variables: { disk, from: source, to: target, moves: moveCount },
      pegs: { A: [...pegs.A], B: [...pegs.B], C: [...pegs.C] },
      moveCount,
      callStack: Array(depth + 1).fill(callId),
      currentCall: depth,
      movingDisk: { disk, from: source, to: target }
    });

    hanoi(numDisks - 1, aux, target, source, depth + 1);
  };

  hanoi(n, 'A', 'C', 'B');

  steps.push({
    line: 12,
    description: `Complete! Total moves: ${moveCount} (optimal: 2^${n} - 1 = ${Math.pow(2, n) - 1})`,
    variables: { totalMoves: moveCount, optimal: Math.pow(2, n) - 1 },
    pegs: { A: [...pegs.A], B: [...pegs.B], C: [...pegs.C] },
    moveCount,
    callStack: [],
    result: moveCount
  });

  return steps;
};

// ============ BACKTRACKING ALGORITHMS ============

export const generateSubsetsSteps = (array) => {
  const steps = [];
  const result = [];
  const current = [];

  steps.push({
    line: 2,
    description: `Generating all subsets of [${array.join(', ')}]`,
    variables: { arrayLength: array.length },
    array: [...array],
    current: [],
    subsets: [],
    included: []
  });

  const backtrack = (index) => {
    // Add current subset to result
    result.push([...current]);
    steps.push({
      line: 5,
      description: `Add current subset: [${current.join(', ')}]`,
      variables: { index, subsetsFound: result.length },
      array: [...array],
      current: [...current],
      subsets: result.map(s => [...s]),
      included: current.map(v => array.indexOf(v)),
      currentIndex: index
    });

    for (let i = index; i < array.length; i++) {
      current.push(array[i]);
      steps.push({
        line: 8,
        description: `Include array[${i}] = ${array[i]}. Current: [${current.join(', ')}]`,
        variables: { i, includedValue: array[i] },
        array: [...array],
        current: [...current],
        subsets: result.map(s => [...s]),
        included: current.map(v => array.indexOf(v)),
        currentIndex: i,
        action: 'include'
      });

      backtrack(i + 1);

      const removed = current.pop();
      steps.push({
        line: 10,
        description: `Backtrack: Remove ${removed}. Current: [${current.join(', ')}]`,
        variables: { removed, i },
        array: [...array],
        current: [...current],
        subsets: result.map(s => [...s]),
        included: current.map(v => array.indexOf(v)),
        currentIndex: i,
        action: 'backtrack'
      });
    }
  };

  backtrack(0);

  steps.push({
    line: 12,
    description: `Complete! Found ${result.length} subsets (2^${array.length} = ${Math.pow(2, array.length)})`,
    variables: { totalSubsets: result.length },
    array: [...array],
    current: [],
    subsets: result.map(s => [...s]),
    included: [],
    result: result.length
  });

  return steps;
};

export const generatePermutationsSteps = (array) => {
  const steps = [];
  const result = [];
  const nums = [...array];

  steps.push({
    line: 2,
    description: `Generating all permutations of [${array.join(', ')}]`,
    variables: { arrayLength: array.length },
    array: [...nums],
    current: [],
    permutations: [],
    fixedIndex: -1
  });

  const permute = (start) => {
    if (start === nums.length) {
      result.push([...nums]);
      steps.push({
        line: 5,
        description: `Complete permutation: [${nums.join(', ')}]`,
        variables: { permutationsFound: result.length },
        array: [...nums],
        current: [...nums],
        permutations: result.map(p => [...p]),
        fixedIndex: nums.length,
        action: 'found'
      });
      return;
    }

    for (let i = start; i < nums.length; i++) {
      // Swap
      if (i !== start) {
        [nums[start], nums[i]] = [nums[i], nums[start]];
        steps.push({
          line: 9,
          description: `Swap positions ${start} and ${i}. Array: [${nums.join(', ')}]`,
          variables: { start, i, swapped: true },
          array: [...nums],
          current: [...nums],
          permutations: result.map(p => [...p]),
          fixedIndex: start,
          swapping: [start, i],
          action: 'swap'
        });
      } else {
        steps.push({
          line: 7,
          description: `Fix position ${start} with value ${nums[start]}`,
          variables: { start, fixedValue: nums[start] },
          array: [...nums],
          current: [...nums],
          permutations: result.map(p => [...p]),
          fixedIndex: start,
          action: 'fix'
        });
      }

      permute(start + 1);

      // Backtrack (undo swap)
      if (i !== start) {
        [nums[start], nums[i]] = [nums[i], nums[start]];
        steps.push({
          line: 12,
          description: `Backtrack: Restore positions ${start} and ${i}. Array: [${nums.join(', ')}]`,
          variables: { start, i, restored: true },
          array: [...nums],
          current: [...nums],
          permutations: result.map(p => [...p]),
          fixedIndex: start,
          swapping: [start, i],
          action: 'backtrack'
        });
      }
    }
  };

  permute(0);

  steps.push({
    line: 15,
    description: `Complete! Found ${result.length} permutations (${array.length}! = ${factorial(array.length)})`,
    variables: { totalPermutations: result.length },
    array: [...array],
    current: [],
    permutations: result.map(p => [...p]),
    fixedIndex: -1,
    result: result.length
  });

  return steps;
};

const factorial = (n) => {
  let r = 1;
  for (let i = 2; i <= n; i++) r *= i;
  return r;
};

export const generateNQueensSteps = (n) => {
  const steps = [];
  const board = Array.from({ length: n }, () => Array(n).fill(0));
  const solutions = [];
  let attempts = 0;
  let backtracks = 0;

  steps.push({
    line: 2,
    description: `N-Queens for ${n}x${n} board. Place ${n} queens with no attacks.`,
    variables: { n },
    board: board.map(r => [...r]),
    solutions: [],
    attempts,
    backtracks,
    currentRow: -1
  });

  const isSafe = (row, col) => {
    // Check column
    for (let i = 0; i < row; i++) {
      if (board[i][col] === 1) return false;
    }
    // Check upper-left diagonal
    for (let i = row - 1, j = col - 1; i >= 0 && j >= 0; i--, j--) {
      if (board[i][j] === 1) return false;
    }
    // Check upper-right diagonal
    for (let i = row - 1, j = col + 1; i >= 0 && j < n; i--, j++) {
      if (board[i][j] === 1) return false;
    }
    return true;
  };

  const solve = (row) => {
    if (row === n) {
      const solution = board.map(r => [...r]);
      solutions.push(solution);
      steps.push({
        line: 5,
        description: `Solution ${solutions.length} found!`,
        variables: { solutionCount: solutions.length },
        board: board.map(r => [...r]),
        solutions: solutions.map(s => s.map(r => [...r])),
        attempts,
        backtracks,
        currentRow: row,
        action: 'solution'
      });
      return;
    }

    for (let col = 0; col < n; col++) {
      attempts++;
      steps.push({
        line: 8,
        description: `Try placing queen at (row=${row}, col=${col})`,
        variables: { row, col, attempts },
        board: board.map(r => [...r]),
        solutions: solutions.map(s => s.map(r => [...r])),
        attempts,
        backtracks,
        currentRow: row,
        tryPosition: { row, col },
        action: 'try'
      });

      if (isSafe(row, col)) {
        board[row][col] = 1;
        steps.push({
          line: 10,
          description: `Safe! Place queen at (${row}, ${col})`,
          variables: { row, col },
          board: board.map(r => [...r]),
          solutions: solutions.map(s => s.map(r => [...r])),
          attempts,
          backtracks,
          currentRow: row,
          placedPosition: { row, col },
          action: 'place'
        });

        solve(row + 1);

        // Backtrack
        board[row][col] = 0;
        backtracks++;
        steps.push({
          line: 13,
          description: `Backtrack: Remove queen from (${row}, ${col})`,
          variables: { row, col, backtracks },
          board: board.map(r => [...r]),
          solutions: solutions.map(s => s.map(r => [...r])),
          attempts,
          backtracks,
          currentRow: row,
          backtrackPosition: { row, col },
          action: 'backtrack'
        });
      } else {
        steps.push({
          line: 15,
          description: `Unsafe at (${row}, ${col}). Skip.`,
          variables: { row, col },
          board: board.map(r => [...r]),
          solutions: solutions.map(s => s.map(r => [...r])),
          attempts,
          backtracks,
          currentRow: row,
          unsafePosition: { row, col },
          action: 'unsafe'
        });
      }
    }
  };

  solve(0);

  steps.push({
    line: 18,
    description: `Complete! Found ${solutions.length} solution(s). Attempts: ${attempts}, Backtracks: ${backtracks}`,
    variables: { solutions: solutions.length, attempts, backtracks },
    board: board.map(r => [...r]),
    solutions: solutions.map(s => s.map(r => [...r])),
    attempts,
    backtracks,
    currentRow: -1,
    result: solutions.length
  });

  return steps;
};
